// Phase 0094-P2 — EPF Compliance page.
//
// Tabs: Monthly Calculation · PF Register · Challan Summary · Joiners/Exits ·
//       UAN/KYC · Forms/Exports · Config

import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, Empty, Input, InputNumber, Modal,
  Row, Select, Skeleton, Space, Table, Tabs, Tag, Typography,
} from 'antd'
import { ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  epfApi, type EPFConfig, type EPFRegisterRow, type KYCRow,
  type WageHeadMapping,
} from '@/features/payroll/epfApi'

const { Text, Title } = Typography

function statusTag(s: string) {
  const c =
    s === 'CALCULATED' ? 'success'
    : s === 'NOT_APPLICABLE' ? 'default'
    : s === 'PROFILE_MISSING' ? 'error'
    : 'warning'
  return <Tag color={c}>{s}</Tag>
}

function RunPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [draft, setDraft] = useState(value)
  return (
    <Space>
      <Input placeholder="Run ID" value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={{ width: 320 }} />
      <Button onClick={() => onChange(draft.trim())} type="primary">Load</Button>
    </Space>
  )
}

export default function EPFPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [runId, setRunId] = useState('')
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(new Date().getMonth() + 1)

  const dashboard = useQuery({
    queryKey: ['epf-dash', runId],
    queryFn: () => epfApi.dashboard(runId),
    enabled: !!runId,
  })
  const register = useQuery({
    queryKey: ['epf-register', runId],
    queryFn: () => epfApi.register(runId),
    enabled: !!runId,
  })
  const challan = useQuery({
    queryKey: ['epf-challan', runId],
    queryFn: () => epfApi.challan(runId),
    enabled: !!runId,
  })
  const joiners = useQuery({
    queryKey: ['epf-joiners', year, month],
    queryFn: () => epfApi.joiners(year, month),
    enabled: !!runId,
  })
  const exits = useQuery({
    queryKey: ['epf-exits', year, month],
    queryFn: () => epfApi.exits(year, month),
    enabled: !!runId,
  })
  const kyc = useQuery({
    queryKey: ['epf-kyc'],
    queryFn: () => epfApi.kyc(),
  })
  const configs = useQuery({
    queryKey: ['epf-configs'],
    queryFn: () => epfApi.listConfigs(),
  })
  const mappings = useQuery({
    queryKey: ['epf-mappings'],
    queryFn: () => epfApi.listMappings(),
  })

  const calcMut = useMutation({
    mutationFn: () => epfApi.calculate(runId),
    onSuccess: (r) => {
      message.success(`EPF calculated: ${r.calculated} OK, ${r.review_required} review, ${r.profile_missing} missing profile.`)
      Object.values({ dashboard, register, challan }).forEach(q => q.refetch())
    },
    onError: (e: Error) => message.error(e.message),
  })

  const ds = dashboard.data
  const summary = ds?.summary

  const registerCols = [
    { title: 'Code', dataIndex: 'emp_code', width: 100 },
    { title: 'Name', dataIndex: 'emp_name', width: 180 },
    { title: 'UAN', dataIndex: 'uan', render: (s: string | null) => s ?? '—' },
    { title: 'EPF Wages', dataIndex: 'epf_wages',
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'EPS Wages', dataIndex: 'eps_wages',
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'Employee EPF', dataIndex: 'employee_epf',
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'Employer EPF', dataIndex: 'employer_epf',
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'Employer EPS', dataIndex: 'employer_eps',
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'EDLI', dataIndex: 'edli',
      render: (n: number) => n > 0 ? `₹${n.toLocaleString()}` : '—' },
    { title: 'Status', dataIndex: 'status', render: statusTag },
  ]

  const kycCols = [
    { title: 'Code', dataIndex: 'emp_code' },
    { title: 'Name', dataIndex: 'emp_name' },
    { title: 'UAN', dataIndex: 'uan', render: (s: string | null) => s ?? <Tag color="error">MISSING</Tag> },
    { title: 'KYC', dataIndex: 'kyc_status' },
    { title: 'Aadhaar', dataIndex: 'aadhaar_seeded',
      render: (b: boolean) => b ? <Tag color="success">✓</Tag> : <Tag color="error">✗</Tag> },
    { title: 'PAN', dataIndex: 'pan_seeded',
      render: (b: boolean) => b ? <Tag color="success">✓</Tag> : <Tag color="error">✗</Tag> },
    { title: 'Bank', dataIndex: 'bank_seeded',
      render: (b: boolean) => b ? <Tag color="success">✓</Tag> : <Tag color="error">✗</Tag> },
    { title: 'Missing', dataIndex: 'missing',
      render: (m: string[]) => m.length ? <Space wrap>{m.map(x => (
        <Tag key={x} color="warning">{x}</Tag>
      ))}</Space> : <Tag color="success">Complete</Tag> },
  ]

  const configCols = [
    { title: 'Code', dataIndex: 'config_code' },
    { title: 'Effective From', dataIndex: 'effective_from' },
    { title: 'Employee %', dataIndex: 'employee_pf_percent' },
    { title: 'Employer %', dataIndex: 'employer_pf_percent' },
    { title: 'EPS %', dataIndex: 'eps_percent' },
    { title: 'Wage Ceiling', dataIndex: 'pf_wage_ceiling',
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'EDLI %', dataIndex: 'edli_percent' },
    { title: 'Admin %', dataIndex: 'epf_admin_charge_percent' },
    { title: 'Source', key: 'src',
      render: (_: unknown, r: EPFConfig) => (
        r.admin_modified_at ? <Tag color="gold">Admin-edited</Tag>
          : r.system_seeded ? <Tag color="blue">Factory</Tag>
          : <Tag>User</Tag>
      ) },
  ]

  const mappingCols = [
    { title: 'Head', dataIndex: 'head_code',
      render: (c: string) => <code>{c}</code> },
    { title: 'Name', dataIndex: 'head_name' },
    { title: 'EPF', dataIndex: 'included_in_epf_wage',
      render: (b: boolean) => b ? <Tag color="success">✓</Tag> : '—' },
    { title: 'EPS', dataIndex: 'included_in_eps_wage',
      render: (b: boolean) => b ? <Tag color="success">✓</Tag> : '—' },
    { title: 'EDLI', dataIndex: 'included_in_edli_wage',
      render: (b: boolean) => b ? <Tag color="success">✓</Tag> : '—' },
    { title: 'Source', key: 'src',
      render: (_: unknown, r: WageHeadMapping) => (
        r.admin_modified_at ? <Tag color="gold">Admin</Tag>
          : r.system_seeded ? <Tag color="blue">Factory</Tag>
          : <Tag>User</Tag>
      ) },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1500, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>EPF Compliance</Title>
      <Text type="secondary">
        Employee + employer PF · EPS · EDLI · admin · challan · ECR-prep · Forms 3A / 5 / 6A / 10 / 11 / 12A / 13 / 19 / 10C.
      </Text>

      <Card style={{ marginTop: 12 }}>
        <Space wrap>
          <Input placeholder="Run ID" value={runId}
                  onChange={(e) => setRunId(e.target.value)}
                  style={{ width: 320 }} />
          <Button type="primary" icon={<ThunderboltOutlined />}
                  disabled={!runId}
                  onClick={() => calcMut.mutate()}
                  loading={calcMut.isPending}>
            Calculate EPF
          </Button>
          <Button icon={<ReloadOutlined />}
                  onClick={() => dashboard.refetch()}>Refresh</Button>
          <InputNumber value={year} onChange={(v) => setYear(Number(v))} min={2020} max={2100} />
          <InputNumber value={month} onChange={(v) => setMonth(Number(v))} min={1} max={12} />
        </Space>
      </Card>

      {runId && summary && (
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col span={6}><Card><Text type="secondary">PF Applicable</Text>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{summary.pf_applicable_employees}</div></Card></Col>
          <Col span={6}><Card><Text type="secondary">PF Wages</Text>
            <div style={{ fontSize: 22, fontWeight: 700 }}>₹{summary.total_epf_wages.toLocaleString()}</div></Card></Col>
          <Col span={6}><Card><Text type="secondary">Employee EPF</Text>
            <div style={{ fontSize: 22, fontWeight: 700 }}>₹{summary.employee_epf.toLocaleString()}</div></Card></Col>
          <Col span={6}><Card><Text type="secondary">Employer Total</Text>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              ₹{(summary.employer_epf + summary.employer_eps + summary.edli + summary.epf_admin_charges + summary.edli_admin_charges).toLocaleString()}</div></Card></Col>
          <Col span={6}><Card><Text type="secondary">Employer EPS</Text>
            <div style={{ fontSize: 22, fontWeight: 700 }}>₹{summary.employer_eps.toLocaleString()}</div></Card></Col>
          <Col span={6}><Card><Text type="secondary">EDLI + Admin</Text>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              ₹{(summary.edli + summary.epf_admin_charges + summary.edli_admin_charges).toLocaleString()}</div></Card></Col>
          <Col span={6}><Card><Text type="secondary">Total Liability</Text>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1677ff' }}>
              ₹{summary.total_liability.toLocaleString()}</div></Card></Col>
          <Col span={6}><Card>
            <Text type="secondary">Review Required</Text>
            <div style={{ fontSize: 22, fontWeight: 700, color: ds.review_required ? '#faad14' : '#52c41a' }}>
              {ds.review_required}</div></Card></Col>
        </Row>
      )}

      {ds && ds.profile_missing > 0 && (
        <Alert style={{ marginTop: 12 }} type="warning" showIcon
                message={`${ds.profile_missing} employee(s) have no EPF profile. Create profiles before lock.`} />
      )}

      <Tabs
        style={{ marginTop: 16 }}
        defaultActiveKey="calc"
        items={[
          { key: 'calc', label: 'Monthly Calculation', children: (
            <Card>
              {!runId ? <Empty description="Enter a Run ID and press Calculate EPF." /> :
                register.isLoading ? <Skeleton active /> :
                <Table rowKey="employee_id" columns={registerCols as never}
                        dataSource={register.data ?? []}
                        size="small" pagination={{ pageSize: 50 }}
                        scroll={{ x: 1300 }} />}
            </Card>
          ) },
          { key: 'register', label: 'PF Register', children: (
            <Card>
              {!runId ? <Empty /> : (
                <Table rowKey="employee_id" columns={registerCols as never}
                        dataSource={register.data ?? []}
                        size="small" pagination={{ pageSize: 50 }}
                        scroll={{ x: 1300 }} />
              )}
            </Card>
          ) },
          { key: 'challan', label: 'Challan Summary', children: (
            <Card>
              {!runId ? <Empty /> : challan.isLoading ? <Skeleton active /> : challan.data && (
                <Row gutter={[12, 12]}>
                  {Object.entries(challan.data).map(([k, v]) => (
                    typeof v === 'number' && (
                      <Col span={8} key={k}>
                        <Card size="small">
                          <Text type="secondary">{k.replace(/_/g, ' ')}</Text>
                          <div style={{ fontSize: 20, fontWeight: 700 }}>
                            ₹{v.toLocaleString()}</div>
                        </Card>
                      </Col>
                    )
                  ))}
                </Row>
              )}
            </Card>
          ) },
          { key: 'joinexit', label: 'Joiners / Exits', children: (
            <>
              <Card title={`Joiners — ${month}/${year}`}>
                <Table rowKey="employee_id"
                        dataSource={joiners.data ?? []}
                        size="small"
                        columns={[
                          { title: 'Code', dataIndex: 'emp_code' },
                          { title: 'Name', dataIndex: 'emp_name' },
                          { title: 'UAN', dataIndex: 'uan' },
                          { title: 'PF #', dataIndex: 'pf_number' },
                          { title: 'PF Join', dataIndex: 'pf_join_date' },
                        ] as never}
                        pagination={false} />
              </Card>
              <Card title={`Exits — ${month}/${year}`} style={{ marginTop: 12 }}>
                <Table rowKey="employee_id"
                        dataSource={exits.data ?? []}
                        size="small"
                        columns={[
                          { title: 'Code', dataIndex: 'emp_code' },
                          { title: 'Name', dataIndex: 'emp_name' },
                          { title: 'UAN', dataIndex: 'uan' },
                          { title: 'PF #', dataIndex: 'pf_number' },
                          { title: 'PF Exit', dataIndex: 'pf_exit_date' },
                        ] as never}
                        pagination={false} />
              </Card>
            </>
          ) },
          { key: 'kyc', label: 'UAN / KYC', children: (
            <Card>
              {kyc.isLoading ? <Skeleton active /> :
                <Table rowKey="employee_id" columns={kycCols as never}
                        dataSource={kyc.data ?? []}
                        size="small" pagination={{ pageSize: 50 }} />}
            </Card>
          ) },
          { key: 'forms', label: 'Forms / Exports', children: (
            <Card>
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                      message="Preparation data — not government portal format. Verify with EPFO portal before filing." />
              <Space wrap>
                <Button disabled={!runId} onClick={async () => {
                  const r = await epfApi.form12a(runId)
                  console.log('Form 12A', r); message.success('Form 12A loaded — see console')
                }}>Form 12A (Monthly)</Button>
                <Button onClick={async () => {
                  const r = await epfApi.form5(year, month)
                  console.log('Form 5', r); message.success(`Form 5: ${r.length} joinees`)
                }}>Form 5 (Joinees)</Button>
                <Button onClick={async () => {
                  const r = await epfApi.form10(year, month)
                  console.log('Form 10', r); message.success(`Form 10: ${r.length} exits`)
                }}>Form 10 (Exits)</Button>
                <Button onClick={async () => {
                  const r = await epfApi.form3a(year)
                  console.log('Form 3A', r); message.success(`Form 3A: ${r.length} employees`)
                }}>Form 3A (Annual)</Button>
                <Button onClick={async () => {
                  const r = await epfApi.form6a(year)
                  console.log('Form 6A', r); message.success('Form 6A loaded — see console')
                }}>Form 6A (Annual statement)</Button>
                <Button disabled={!runId} onClick={async () => {
                  const r = await epfApi.ecrExport(runId)
                  console.log('ECR-like', r); message.success(`ECR rows: ${r.length}`)
                }}>ECR-like export</Button>
              </Space>
            </Card>
          ) },
          { key: 'config', label: 'Config', children: (
            <>
              <Card title="EPF Config Master">
                {configs.isLoading ? <Skeleton active /> :
                  <Table rowKey="id" columns={configCols as never}
                          dataSource={configs.data ?? []}
                          size="small" pagination={false} />}
              </Card>
              <Card title="Wage Head Mapping" style={{ marginTop: 12 }}>
                {mappings.isLoading ? <Skeleton active /> :
                  <Table rowKey="id" columns={mappingCols as never}
                          dataSource={mappings.data ?? []}
                          size="small" pagination={{ pageSize: 50 }} />}
              </Card>
            </>
          ) },
        ]}
      />
    </div>
  )
}
